import React, { useState } from "react";
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    Avatar,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    TableContainer,
    IconButton,
    Menu,
    MenuItem,
    Tooltip,
    LinearProgress,
    Chip,
    TextField,
    InputAdornment,
    Button,
    Tabs,
    Tab
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";
import CreateUserStoryDialog from "./CreateUserStory";


// ---------------------------------------------------
// MOCK DATA
// ---------------------------------------------------
const mockStories = [
    {
        id: 1,
        story_name: "Location Validation for Delivery Zones",
        eptus: 13,
        assigned_to: "All",
        status: "Medium",
        tasks_progress: { completed: 5, total: 7 },
        progress_percent: 71,
        due_date: "2025-12-15",
    },
    {
        id: 2,
        story_name: "Share-Ur-Truck App",
        eptus: 2,
        assigned_to: "In Development",
        status: "High",
        tasks_progress: { completed: 0, total: 4 },
        progress_percent: 0,
        due_date: "2025-11-20",
    },
    {
        id: 3,
        story_name: "CommuniLink Beta",
        eptus: 1,
        assigned_to: "Ready for Dev",
        status: "Medium",
        tasks_progress: { completed: 0, total: 3 },
        progress_percent: 0,
        due_date: "2025-11-10",
    },
];

// ---------------------------------------------------
// SUMMARY MOCK CARDS
// ---------------------------------------------------
const cardDataMock = [
    { title: "Total Stories", gradient: "#6366f1", count: 12, key: "total" },
    { title: "Ready for Dev", gradient: "#0891b2", count: 2, key: "ready" },
    { title: "In Development", gradient: "#f59e0b", count: 8, key: "development" },
    { title: "Completed", gradient: "#22c55e", count: 18, key: "completed" },
];

const getLetter = (str) => (str ? str.charAt(0).toUpperCase() : "?");

export default function UserStoriesDashboard() {
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [stories] = useState(mockStories);
    const [searchTerm, setSearchTerm] = useState("");
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedStory, setSelectedStory] = useState(null);
    const [openCreateDialog, setOpenCreateDialog] = useState(false);

    const [tabValue, setTabValue] = useState("all");

    const handleMenuOpen = (e, story) => {
        setAnchorEl(e.currentTarget);
        setSelectedStory(story);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedStory(null);
    };

    const filteredStories = stories.filter((story) =>
        story.story_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Box sx={{ p: 3 }}>
            {/* ------------------------------------ */}
            {/* PAGE HEADER */}
            {/* ------------------------------------ */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 4,
                    flexWrap: "wrap",
                    gap: 2,
                }}
            >
                <Box>
                    <Typography sx={{ fontSize: "1.9rem", fontWeight: 700 }}>
                        Stories Overview
                    </Typography>
                    <Typography sx={{ fontSize: "0.9rem", color: "#64748b" }}>
                        Your sprint stories summary
                    </Typography>
                </Box>

                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenCreateDialog(true)}
                    sx={{
                        background: "#6366f1",
                        textTransform: "none",
                        fontWeight: 600,
                        px: 3,
                        py: 1,
                        borderRadius: "10px",
                        "&:hover": { background: "#4f46e5" },
                    }}
                >
                    Create Story
                </Button>

            </Box>

            {/* ------------------------------------ */}
            {/* TOGGLE (TABS) */}
            {/* ------------------------------------ */}
            <Tabs
                value={tabValue}
                onChange={(e, v) => setTabValue(v)}
                sx={{ mb: 3 }}
                TabIndicatorProps={{
                    sx: { backgroundColor: "#6366f1" },
                }}
            >
                <Tab
                    value="all"
                    label="All Stories"
                    sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        color: tabValue === "all" ? "#6366f1" : "#475569",
                    }}
                />
                <Tab
                    value="my"
                    label="My User Stories"
                    sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        color: tabValue === "my" ? "#6366f1" : "#475569",
                    }}
                />
            </Tabs>

            {/* ------------------------------------ */}
            {/* OVERVIEW CARDS */}
            {/* ------------------------------------ */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {cardDataMock.map((card) => (
                    <Grid item xs={12} sm={6} md={3} key={card.key}>
                        <Card
                            onClick={() => setSelectedCategory(card.key)}
                            sx={{
                                borderRadius: "20px",
                                cursor: "pointer",
                                transition: "0.3s",
                                p: 1,
                                border:
                                    selectedCategory === card.key
                                        ? `2px solid ${card.gradient}`
                                        : "2px solid transparent",
                                boxShadow:
                                    selectedCategory === card.key
                                        ? `0 10px 25px ${card.gradient}30`
                                        : "0 3px 8px rgba(0,0,0,0.05)",
                                "&:hover": { transform: "translateY(-5px)" },
                            }}
                        >
                            <CardContent>
                                <Typography sx={{ fontWeight: 600, fontSize: "1rem" }}>
                                    {card.title}
                                </Typography>

                                <Typography
                                    sx={{
                                        fontSize: "2.3rem",
                                        fontWeight: 700,
                                        mt: 1,
                                        mb: 1,
                                        color: "#1e293b",
                                    }}
                                >
                                    {card.count}
                                </Typography>

                                <LinearProgress
                                    variant="determinate"
                                    value={selectedCategory === card.key ? 100 : 0}
                                    sx={{
                                        height: 5,
                                        borderRadius: 2,
                                        background: "#e2e8f0",
                                        "& .MuiLinearProgress-bar": {
                                            background: card.gradient,
                                        },
                                    }}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* ------------------------------------ */}
            {/* FILTER BAR */}
            {/* ------------------------------------ */}
            <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
                <TextField
                    placeholder="Search by Story Name..."
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ width: "280px" }}
                />

                <Button
                    variant="outlined"
                    color="error"
                    onClick={() => setSearchTerm("")}
                >
                    Clear Filters
                </Button>
            </Box>

            {/* ------------------------------------ */}
            {/* DESKTOP TABLE */}
            {/* ------------------------------------ */}
            <TableContainer
                component={Paper}
                sx={{
                    borderRadius: "12px",
                    display: { xs: "none", md: "block" },
                }}
            >
                <Table>
                    <TableHead>
                        <TableRow>
                            {[
                                "Story Name",                           
                               
                                "Priority",
                                "Tasks Progress",
                                "Due Date",
                                "Actions",
                            ].map((col) => (
                                <TableCell
                                    key={col}
                                    sx={{
                                        background: "#f8fafc",
                                        fontWeight: 600,
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {col}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {filteredStories.map((story) => (
                            <TableRow key={story.id} hover>
                                {/* Story Name */}
                                <TableCell>
                                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                                        <Avatar>{getLetter(story.story_name)}</Avatar>
                                        <Typography>{story.story_name}</Typography>
                                    </Box>
                                </TableCell>

                                {/* <TableCell>{story.eptus}</TableCell>
                                <TableCell>{story.assigned_to}</TableCell> */}

                                {/* Status Badge */}
                                <TableCell>
                                    <Chip
                                        label={story.status}
                                        sx={{
                                            background:
                                                story.status === "High"
                                                    ? "#fee2e2"
                                                    : story.status === "Medium"
                                                        ? "#fef9c3"
                                                        : "#dcfce7",
                                            color:
                                                story.status === "High"
                                                    ? "#ef4444"
                                                    : story.status === "Medium"
                                                        ? "#eab308"
                                                        : "#22c55e",
                                            fontWeight: 600,
                                        }}
                                    />
                                </TableCell>

                                {/* Progress */}
                                <TableCell>
                                    <Box sx={{ width: "100px" }}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={story.progress_percent}
                                            sx={{
                                                height: 6,
                                                borderRadius: 3,
                                            }}
                                        />
                                        <Typography
                                            sx={{
                                                textAlign: "right",
                                                fontSize: "12px",
                                                mt: 0.5,
                                                fontWeight: 600,
                                            }}
                                        >
                                            {story.progress_percent}%
                                        </Typography>
                                    </Box>
                                </TableCell>

                                <TableCell>{story.due_date}</TableCell>

                                {/* Actions */}
                                <TableCell>
                                    <IconButton onClick={(e) => handleMenuOpen(e, story)}>
                                        <MoreVertIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* ACTION MENU */}
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                <MenuItem onClick={handleMenuClose}>Edit Story</MenuItem>
                <MenuItem onClick={handleMenuClose}>View Tasks</MenuItem>
                <MenuItem onClick={handleMenuClose}>Delete</MenuItem>
            </Menu>

            {/* ------------------------------------ */}
            {/* MOBILE CARDS */}
            {/* ------------------------------------ */}
            <Box sx={{ display: { xs: "block", md: "none" }, mt: 3 }}>
                {filteredStories.map((story) => (
                    <Card key={story.id} sx={{ mb: 2, p: 2 }}>
                        <Box sx={{ display: "flex", gap: 2 }}>
                            <Avatar>{getLetter(story.story_name)}</Avatar>
                            <Box>
                                <Typography sx={{ fontWeight: 600 }}>
                                    {story.story_name}
                                </Typography>
                                <Typography sx={{ fontSize: "12px", color: "#64748b" }}>
                                    Eptus: {story.eptus}
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ mt: 2 }}>
                            <Chip label={story.status} size="small" sx={{ mb: 1 }} />
                            <LinearProgress
                                variant="determinate"
                                value={story.progress_percent}
                                sx={{ height: 5, borderRadius: 2 }}
                            />
                        </Box>

                        <Typography sx={{ fontSize: "12px", mt: 1 }}>
                            Due Date: {story.due_date}
                        </Typography>

                        <Box sx={{ textAlign: "right" }}>
                            <IconButton onClick={(e) => handleMenuOpen(e, story)}>
                                <MoreVertIcon />
                            </IconButton>
                        </Box>
                    </Card>
                ))}
            </Box>

            <CreateUserStoryDialog
    open={openCreateDialog}
    onClose={() => setOpenCreateDialog(false)}
/>

        </Box>
    );
}
